import React from 'react';
import type { DesignWiring, ZoneDropData } from '../designWiring';
import {
  BAND_LABEL,
  COLUMN_LABEL,
  type BandKind,
  type PageVariant,
  type ZoneColumn,
} from '../formaZonesModel';

/* ─── One band of the paper as a Design-mode object: click to select the
       zone, drag a chip over it to get three SOLID column targets with a
       snap highlight and an insertion caret. ─── */
export function ZoneShell({
  band,
  design,
  empty,
  variant,
  children,
}: {
  band: BandKind;
  design: DesignWiring;
  /** Pages view: the page variant this band SHOWS (each page wears its own
   *  band). Every gesture here edits that variant. Absent (Continuous) → the
   *  edited variant, exactly as before. */
  variant?: PageVariant;
  /** No tokens in any of this band's three columns (for the placeholder). */
  empty: boolean;
  children: React.ReactNode;
}) {
  const [overCol, setOverCol] = React.useState<ZoneColumn | null>(null);
  const [dragOver, setDragOver] = React.useState(false);
  const selected =
    design.styleSel?.kind === 'zone' &&
    design.styleSel.band === band &&
    (variant == null || variant === design.pageVariant);
  const selectThis = () => design.onStyleSelect({ kind: 'zone', band, variant });

  const parseDrag = (e: React.DragEvent): ZoneDropData | null => {
    try {
      const raw = e.dataTransfer.getData('application/json');
      if (!raw) return null;
      return JSON.parse(raw) as ZoneDropData;
    } catch {
      return null;
    }
  };

  const colFromX = (e: React.DragEvent): ZoneColumn => {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const t = (e.clientX - r.left) / Math.max(1, r.width);
    return t < 1 / 3 ? 'L' : t < 2 / 3 ? 'C' : 'R';
  };

  const finishDrop = (e: React.DragEvent, col: ZoneColumn) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    setOverCol(null);
    const data = parseDrag(e);
    if (data) design.onZoneDrop(band, col, data, variant);
  };

  const showTargets = design.dragActive || dragOver;
  // Slot targeting (Studio sheet): a band with tokens in it takes the drop at a
  // SLOT, so the column-thirds overlay is only for an empty band — and for the
  // Designer's read-only sheet, which has no slot wiring.
  const slotAware = !!(design.onZoneInsertAt || design.onZoneReplaceAt);
  const showThirds = showTargets && (empty || !slotAware);

  // A drop anywhere, or the drag ending (Esc included), puts the lit band out —
  // a slot drop is handled deeper and never reaches this shell's own onDrop.
  React.useEffect(() => {
    if (!dragOver) return;
    const off = () => {
      setDragOver(false);
      setOverCol(null);
    };
    window.addEventListener('dragend', off, true);
    window.addEventListener('drop', off, true);
    return () => {
      window.removeEventListener('dragend', off, true);
      window.removeEventListener('drop', off, true);
    };
  }, [dragOver]);

  return (
    <div
      className={[
        'dsg-zoneshell',
        `dsg-zoneshell-${band}`,
        selected ? 'is-selected' : '',
        showTargets ? 'is-droppable' : '',
        dragOver ? 'is-lit' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      role="button"
      tabIndex={0}
      aria-label={`${BAND_LABEL[band]} band — click to edit`}
      aria-pressed={selected}
      // Stop at mousedown too: the sheet clears the zone selection on
      // mousedown, and a band must not clear itself on the way to selecting.
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        selectThis();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          selectThis();
        }
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
        setOverCol(colFromX(e));
      }}
      onDragLeave={(e) => {
        if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) {
          setDragOver(false);
          setOverCol(null);
        }
      }}
      onDrop={(e) => finishDrop(e, overCol ?? colFromX(e))}
    >
      <span className="dsg-zoneshell-tag" aria-hidden="true">
        {BAND_LABEL[band]}
      </span>
      {/* Always-on whisper affordance (Task 4, 2026-08-21) — a gutter tick
          so every zone boundary is discoverable at rest, not just on hover
          (the on-hover pill above still carries the name). Decorative (the
          band itself already carries the real label and click target), so
          it's aria-hidden. */}
      <span className="dsg-zonerail" aria-hidden="true">
        <span className="dsg-zonerail-tick" />
      </span>
      {empty ? (
        // Order 1 (Josh, 2026-08-13): the Studio has no zone panel — the
        // empty-band hint there adds text right on the sheet. One button per
        // COLUMN, so an empty band's Left and Right are reachable without a
        // drag (they used to be, through the classic canvas's three slots).
        // The Designer passes no `onZoneQuickAdd`, so its empty-hint stays
        // exactly the plain, non-interactive div it always was.
        design.onZoneQuickAdd ? (
          <div className="dsg-zoneshell-empty dsg-zoneshell-empty-cols">
            <span className="dsg-zoneshell-empty-label">
              {BAND_LABEL[band]} is empty — add text
            </span>
            <span className="dsg-zoneshell-empty-actions">
              {(['L', 'C', 'R'] as ZoneColumn[]).map((c) => (
                <button
                  key={c}
                  type="button"
                  className="dsg-zoneshell-empty-btn"
                  aria-label={`Add text to the ${COLUMN_LABEL[c].toLowerCase()} of the ${BAND_LABEL[band].toLowerCase()}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    selectThis();
                    design.onZoneQuickAdd?.(band, c, variant);
                  }}
                >
                  {COLUMN_LABEL[c]}
                </button>
              ))}
            </span>
          </div>
        ) : (
          <div className="dsg-zoneshell-empty">
            {BAND_LABEL[band]} is empty — drop tokens here, or click to edit
          </div>
        )
      ) : (
        children
      )}
      {showThirds && (
        <div className="dsg-zonedrop" aria-hidden="true">
          {(['L', 'C', 'R'] as ZoneColumn[]).map((c) => (
            <div
              key={c}
              className={`dsg-zonedrop-col${overCol === c ? ' is-over' : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragOver(true);
                setOverCol(c);
              }}
              onDrop={(e) => finishDrop(e, c)}
            >
              <span className="dsg-zonedrop-label">
                {overCol === c ? `Add to ${COLUMN_LABEL[c].toLowerCase()}` : COLUMN_LABEL[c]}
              </span>
              {overCol === c && <span className="dsg-zonedrop-caret" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
